<template>
  <div class="card mb-2" ref="card">
    <div class="card-header bg-primary text-white d-flex">
      <a v-b-toggle="`collapse-associated-properties-card`"
         class="collapse-icon c-pointer">
        <font-awesome-icon class="when-opened" icon='caret-down' size="lg" />
        <font-awesome-icon class="when-closed fa-w-10" icon='caret-right' size="lg" />
      </a>
      <span class="font-weight-bold">
        {{title}}
      </span>
      <span class="ml-auto c-pointer white-space-nowrap"
        @click="startManaging"
        v-if="!$store.getters['User/isCurrentUserViewOnly'] && canManageProperties && parentNamespace === 'Instrument'"
      >
        + Add/Remove
      </span>
      <span class="ml-auto c-pointer white-space-nowrap"
        @click="startManaging"
        v-if="$store.getters['User/isCurrentUserAdmin'] && parentNamespace === 'Service'"
      >
        Edit
      </span>
    </div>
    <b-collapse :id="`collapse-associated-properties-card`" v-model="showCollapse">
      <div class="card-body">
        <LoadingSpinner v-if="loading" message ='Loading Associated Properties' size = '1x'>
        </LoadingSpinner>
        <template v-if="!loading">
          <ManageAssociatedProperties
            v-if="isCurrentlyManaging"
            @closeForm="stopManaging"
            :parentNamespace="parentNamespace"
            :storeNamespace="storeNamespace"
          />
          <div v-if="parentNamespace === 'Instrument' || (parentNamespace === 'Service' && !isCurrentlyManaging)">
            <div v-for='entity in visibleEntities' :key="entity.id" >
              <template v-if="entity.id == firstCrossProjectPropertyId">
                <hr>
                <div class="text-primary font-weight-bold mb-3">
                  Cross-Project Properties
                </div>
              </template>
              <div class="card listing-card">
                <EditAssociatedProperty
                  v-if="currentlyEditingIds && currentlyEditingIds.some(id => id === entity.id)"
                  :property="entity"
                  @closeForm="stopEditing"
                  :storeNamespace="storeNamespace"
                />
                <ViewAssociatedProperty v-else
                  :property="entity"
                  :storeNamespace="storeNamespace"
                  @edit="startEditing"
                />
              </div>
            </div>
          </div>
          <PaginationControls
           :orderedEntities="orderedEntities"
           :initialPageSize="initialPageSize"
           :pageSize="pageSize"
           :pageSizeOptions="pageSizeOptions"
           :currentPage="currentPage"
           :firstVisibleEntry="firstVisibleEntry"
           :lastVisibleEntry="lastVisibleEntry"
           :rows="rows"
           @update-current-page="updateCurrentPage"
           @update-page-size="updatePageSize">
          </PaginationControls>
        </template>
      </div>
    </b-collapse>
  </div>
</template>

<script>
import { LookupModules, EntityModules } from '@/store/moduleConfig';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import ViewAssociatedProperty from './ViewAssociatedProperty.vue';
import ManageAssociatedProperties from './ManageAssociatedProperties.vue';
import EditAssociatedProperty from './EditAssociatedProperty.vue';
import CollapseControl from '@/mixins/CollapseControl';
import Pagination from '@/mixins/Pagination';
import PaginationControls from '../common/PaginationControls.vue';

export default {
  name: 'AssociatedPropertiesCard',
  props: {
    storeNamespace: String,
    parentNamespace: {
      type: String,
      default: 'Instrument',
    },
    canManageProperties: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    EditAssociatedProperty,
    LoadingSpinner,
    ManageAssociatedProperties,
    ViewAssociatedProperty,
    PaginationControls,
  },
  mixins: [CollapseControl, Pagination],
  data() {
    return {
      currentlyEditingIds: [],
      isCurrentlyManaging: false,
      showCollapse: false,
    };
  },
  created() {
    this.$store.dispatch(`${LookupModules.PROPERTY_STATUSES.namespace}/fetchOptions`, LookupModules.PROPERTY_STATUSES.url);
  },
  computed: {
    entities() {
      return this.$store.state[this.storeNamespace].entities;
    },
    loading() {
      return this.$store.state[this.storeNamespace].loading;
    },
    title() {
      if (this.storeNamespace === EntityModules.ASSOCIATED_PROPERTIES.namespace) return 'Associated Properties with Impacted Acres & Development Rights';
      return 'Associated Properties';
    },
    firstCrossProjectPropertyId() {
      const property = this.orderedEntities
        .find(entity => entity.projectId !== parseInt(this.$route.params.project_id, 10));
      if (property) return property.id;
      return undefined;
    },
  },
  methods: {
    entitySort(a, b) {
      const projectCompare = b.projectId - a.projectId;
      if (projectCompare !== 0) {
        if (a.projectId === parseInt(this.$route.params.project_id, 10)) {
          // Properties for the route's project sorts first
          return -1;
        }
        if (b.projectId === parseInt(this.$route.params.project_id, 10)) {
          // Property b matches the route's project, so it is sorted first
          return 1;
        }
        return projectCompare;
      }
      const statusCompare = a.statusId - b.statusId;
      if (statusCompare === 0) {
        const acresCompare = b.calculatedAcres - a.calculatedAcres;
        return acresCompare || b.id - a.id;
      }
      return statusCompare;
    },
    startManaging() {
      this.isCurrentlyManaging = true;
      this.showCollapse = true;
      this.$store.dispatch('Common/startEditMode', this.storeNamespace);
    },
    stopManaging() {
      this.isCurrentlyManaging = false;
      this.$store.dispatch('Common/stopEditMode', this.storeNamespace);
      this.$refs.card.scrollIntoView();
    },
    startEditing(id) {
      this.currentlyEditingIds.push(id);
      this.$store.dispatch('Common/startEditMode', this.storeNamespace);
    },
    stopEditing(id) {
      this.$store.dispatch('Common/stopEditMode', this.storeNamespace);
      this.currentlyEditingIds = this.currentlyEditingIds.filter(thisId => thisId !== id);
      this.$refs.card.scrollIntoView();
    },
  },
};
</script>
